import React, { ReactElement, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Application } from "../Application";

import useForceUpdate from "use-force-update";
import { object, string } from "yup";
import { Payment, ProofOfPaymentForm } from "../../payments";
import { InputErrorMessage } from "../../ui";
import { ApplicationService } from "../ApplicationService";

interface IProps {
	application: Application;
	jumpToStep?: any;
	nextStep: number;
	payment: Payment;
}

export const StepPaymentOptions = (props: IProps): ReactElement => {
	const [errors, setErrors] = useState(null);
	const [validateProof, setValidateProof] = useState(0);
	const forceUpdate = useForceUpdate();

	const applicationService = new ApplicationService();
	const application = props.application;

	const courseFeePaymentOptions = [
		"Full Payment",
		"Financing",
		"Company Sponsorship",
	];

	const alreadyPaidOptions = ["Yes", "Not yet", "How do I pay?"];

	const validateStep = (paymentProof?: Payment, paymentFiles?: File[]) => {
		let schemaOptions: any;
		let schemaObject: any;

		if (application.applyingAs === "A Company") {
			application.courseFeeHasMadePayment = "No";
			application.sponsorCompanyName = application.companyName;
		}

		if (application.courseFeePaymentOption !== courseFeePaymentOptions[2]) {
			schemaOptions = {
				courseFeePaymentOption: string().required(
					"Please select a payment option",
				),
				courseFeeHasMadePayment: string().required(
					"Please indicate if you have made a payment",
				),
			};

			schemaObject = {
				courseFeePaymentOption: application.courseFeePaymentOption,
				courseFeeHasMadePayment: application.courseFeeHasMadePayment,
			};
		} else {
			schemaOptions = {
				courseFeePaymentOption: string().required(
					"Please select a payment option",
				),
				courseFeeHasMadePayment: string().required(
					"Please indicate if you have made a payment",
				),
				sponsorCompanyName: string().required(
					"Please enter the sponsor company name",
				),
				sponsorAuthFullname: string().required(
					"Please enter the authorising personnel full name",
				),
				sponsorAuthPosition: string().required(
					"Please enter the authorising personnel position",
				),
				sponsorAuthPhone: string().required(
					"Please enter the authorising personnel phone number",
				),
				sponsorAuthEmail: string().required(
					"Please enter the authorising personnel email",
				),
				sponsorLevel: string().required(
					"Please indicate the sponsor level",
				),
			};

			schemaObject = {
				courseFeePaymentOption: application.courseFeePaymentOption,
				courseFeeHasMadePayment: application.courseFeeHasMadePayment,
				sponsorCompanyName: application.sponsorCompanyName,
				sponsorAuthFullname: application.sponsorAuthFullname,
				sponsorAuthPosition: application.sponsorAuthPosition,
				sponsorAuthPhone: application.sponsorAuthPhone,
				sponsorAuthEmail: application.sponsorAuthEmail,
				sponsorLevel: application.sponsorLevel,
			};
		}

		const schema = object(schemaOptions);

		schema
			.validate(schemaObject, { abortEarly: false })
			.then(valid => {
				if (valid) {
					setErrors(null);
					if (props.application.currentStep < 5) {
						props.application.currentStep = 5;
					}
					applicationService.updateApplication(application);
					if (paymentProof) {
						applicationService.submitProofOfPayment(
							paymentProof,
							paymentFiles,
						);
					}
					props.jumpToStep(props.nextStep);
				}
			})
			.catch(err => {
				setErrors(err);
			});
	};

	const paymentInformation = () => {
		return (
			<>
				<h3 className="text-center mb-4 mt-4">PAYMENT OPTIONS</h3>
				<p>
					Make payment in full or multiple partial payments at any
					time, in any amount, until registration and course fee is{" "}
					<u>received in full prior to the start date</u> or request a
					UCC payment plan.
				</p>
				<p>
					You may choose to apply for third party student financing
					and submit approval to UCC along with your application.
				</p>
				<p>
					Submit employer or other sponsor approval for payment in
					full or for part of the required fee.
				</p>
				<ul>
					<li>
						Payments are accepted at any UCC campus from a debit or
						credit card.
					</li>
					<li>
						Cashiers will not accept cash payments exceeding $2,000
						JMD. Personal checks are not accepted.
					</li>
					<li>Bank deposits or transfers can be made to UCC:</li>
				</ul>
				<p className="text-center mt-4">
					<strong>Bank Name:</strong> National Commercial Bank <br />
					<strong>Account Name:</strong> University of The
					Commonwealth Caribbean
					<br />
					<strong>Account:</strong> NCB Savings Account # 435300928
					<br />
					<strong>Branch:</strong> St. James Street, Montego Bay
					<br />
				</p>
			</>
		);
	};

	const proofOfPayment = () => {
		return (
			<Row className="mb-4">
				<Col>
					<h5 className="mt-3 mb-3">Proof of Payment *</h5>
					<ProofOfPaymentForm
						payment={props.payment}
						submitDisabled={false}
						manualValidate={validateProof}
						onSubmit={validateStep}
						showSubmit={false}
					/>
				</Col>
			</Row>
		);
	};

	const clearSponsorOptions = () => {
		application.sponsorCompanyName = "";
		application.sponsorAuthFullname = "";
		application.sponsorAuthPosition = "";
		application.sponsorAuthPhone = "";
		application.sponsorAuthEmail = "";
		application.sponsorLevel = "";
	};

	const companySponsorOptions = () => {
		return (
			<>
				{application.applyingAs !== "A Company" ? (
					<Row>
						<Col xs={12} sm={8} className="mb-4">
							<Form.Group controlId="sponsorCompanyName">
								<Form.Label>Company Sponsoring *</Form.Label>
								<Form.Control
									size="lg"
									defaultValue={
										application.sponsorCompanyName
									}
									onChange={(event: any) => {
										if (application.lead) {
											application.sponsorCompanyName =
												event.target.value;
											forceUpdate();
										}
									}}
								/>
								<InputErrorMessage
									errors={errors}
									field="sponsorCompanyName"
								/>
							</Form.Group>
						</Col>
					</Row>
				) : null}
				<strong className="mb-4" style={{ display: "block" }}>
					Authorising Personnel
				</strong>
				<Row className="mb-3">
					<Col xs={12} sm={6}>
						<Form.Group controlId="sponsorAuthFullname">
							<Form.Label>Full Name *</Form.Label>
							<Form.Control
								size="lg"
								defaultValue={application.sponsorAuthFullname}
								onChange={(event: any) => {
									if (application.lead) {
										application.sponsorAuthFullname =
											event.target.value;
										forceUpdate();
									}
								}}
							/>
							<InputErrorMessage
								errors={errors}
								field="sponsorAuthFullname"
							/>
						</Form.Group>
					</Col>
					<Col xs={12} sm={6}>
						<Form.Group controlId="sponsorAuthPosition">
							<Form.Label>Position *</Form.Label>
							<Form.Control
								size="lg"
								defaultValue={application.sponsorAuthPosition}
								onChange={(event: any) => {
									if (application.lead) {
										application.sponsorAuthPosition =
											event.target.value;
										forceUpdate();
									}
								}}
							/>
							<InputErrorMessage
								errors={errors}
								field="sponsorAuthPosition"
							/>
						</Form.Group>
					</Col>
				</Row>
				<Row className="mb-3">
					<Col xs={12} sm={6}>
						<Form.Group controlId="sponsorAuthPhone">
							<Form.Label>
								Authorising Personnel Telephone *
							</Form.Label>
							<Form.Control
								size="lg"
								defaultValue={application.sponsorAuthPhone}
								onChange={(event: any) => {
									if (application.lead) {
										application.sponsorAuthPhone =
											event.target.value;
										forceUpdate();
									}
								}}
							/>
							<InputErrorMessage
								errors={errors}
								field="sponsorAuthPhone"
							/>
						</Form.Group>
					</Col>
					<Col xs={12} sm={6}>
						<Form.Group controlId="sponsorAuthEmail">
							<Form.Label>
								Authorising Personnel Email *
							</Form.Label>
							<Form.Control
								size="lg"
								defaultValue={application.sponsorAuthEmail}
								onChange={(event: any) => {
									if (application.lead) {
										application.sponsorAuthEmail =
											event.target.value;
										forceUpdate();
									}
								}}
							/>
							<InputErrorMessage
								errors={errors}
								field="sponsorAuthEmail"
							/>
						</Form.Group>
					</Col>
				</Row>
				<Row className="mb-3">
					<Col sm={12} className="mt-3">
						<Form.Label className="mb-4">
							Level of Sponsorship
						</Form.Label>
						{["Complete", "Partial"].map((option, idx) => (
							<div key={`los-${idx}`} className="mb-3">
								<Form.Check
									type="radio"
									id={`los-${idx}`}
									name="sponsorLevel"
									label={option}
									value={option}
									defaultChecked={
										application.sponsorLevel === option
									}
									onChange={() => {
										props.application.sponsorLevel = option;
										forceUpdate();
									}}
								/>
							</div>
						))}
						<InputErrorMessage
							field="sponsorLevel"
							errors={errors}
						/>
					</Col>
				</Row>
			</>
		);
	};

	return (
		<>
			{application.applyingAs !== "A Company" ? (
				<Row>
					<Col sm={12} className="mt-3 mb-3">
						<Form.Label className="mb-4">
							Course Fee Payment Option *
						</Form.Label>
						<Row>
							{courseFeePaymentOptions.map((option, idx) => (
								<Col sm={6}>
									<div
										key={`default-${idx}`}
										className="mb-3"
									>
										<Form.Check
											type="radio"
											id={`default-${idx}`}
											name="courseFeePaymentOption"
											label={option}
											value={option}
											defaultChecked={
												application.courseFeePaymentOption ===
												option
											}
											onChange={() => {
												props.application.courseFeePaymentOption = option;
												if (
													option !==
													courseFeePaymentOptions[2]
												) {
													clearSponsorOptions();
												}
												forceUpdate();
											}}
										/>
									</div>
								</Col>
							))}
						</Row>
						<InputErrorMessage
							field="courseFeePaymentOption"
							errors={errors}
						/>
					</Col>
				</Row>
			) : null}

			{application.courseFeePaymentOption &&
			application.courseFeePaymentOption === courseFeePaymentOptions[2]
				? companySponsorOptions()
				: null}
			{application.applyingAs !== "A Company" ? (
				<Row>
					<Col sm={12} className="mt-3">
						<Form.Label className="mb-4">
							Have you already made payment? *
						</Form.Label>
						<Row>
							{alreadyPaidOptions.map((option, idx) => (
								<Col sm={6}>
									<div key={`po-${idx}`} className="mb-3">
										<Form.Check
											type="radio"
											id={`po-${idx}`}
											name="courseFeeHasMadePayment"
											label={option}
											value={option}
											defaultChecked={
												application.courseFeeHasMadePayment ===
												option
											}
											onChange={() => {
												props.application.courseFeeHasMadePayment = option;
												forceUpdate();
											}}
										/>
									</div>
								</Col>
							))}
						</Row>
						<InputErrorMessage
							field="courseFeeHasMadePayment"
							errors={errors}
						/>
					</Col>
				</Row>
			) : null}

			{application.courseFeeHasMadePayment &&
			application.courseFeeHasMadePayment === alreadyPaidOptions[0]
				? proofOfPayment()
				: null}
			{application.courseFeeHasMadePayment &&
			application.courseFeeHasMadePayment !== alreadyPaidOptions[0] &&
			application.applyingAs !== "A Company"
				? paymentInformation()
				: null}

			<Row className="mt-3">
				<Col>
					<Button
						variant="primary"
						size="lg"
						className="pull-left btn-next"
						onClick={() => props.jumpToStep(props.nextStep - 2)}
					>
						Previous
					</Button>
					<Button
						variant="primary"
						size="lg"
						className="float-right btn-next"
						onClick={() => {
							if (
								application.courseFeeHasMadePayment ===
								alreadyPaidOptions[0]
							) {
								setValidateProof(validateProof + 1);
							} else {
								validateStep();
							}
						}}
					>
						Next
					</Button>
				</Col>
			</Row>
		</>
	);
};
